import { hasOwnProperty } from "@glideapps/ts-necessities";
import type { BasePrimitiveValue } from "../common/components/computation-types";
import { isBasePrimitiveValue } from "../common/components/computation-types";
import type { Icon } from "../common/components/icons";
import type { TableName } from "../common/database-strings";
import type {
    ActionDescription,
    ArrayFilter,
    LegacyPropertyDescription,
    PaymentMethod,
    PropertyDescription,
    SourceColumn,
    SpecialValueKind,
} from "../common/description";
import {
    getDefaultContextColumnName,
    isPaymentMethod,
    isSourceColumn,
    makeSourceColumn,
    makeTableName,
    PropertyKind,
} from "../common/description";
import { checkArray, checkBoolean, checkNumber, checkString, isArray, panic } from "../support";

// FIXME: Do this check properly, with validation via the description handlers
export function isPropertyDescription(x: unknown): x is PropertyDescription {
    if (!hasOwnProperty(x, "kind")) return false;
    return typeof x.kind === "string";
}

export function makeSourceColumnProperty(sourceColumn: SourceColumn): PropertyDescription {
    return { kind: PropertyKind.Column, value: sourceColumn };
}

export function makeColumnProperty(columnName: string): PropertyDescription;
export function makeColumnProperty(columnName: string | undefined): PropertyDescription | undefined;
export function makeColumnProperty(columnName: string | undefined): PropertyDescription | undefined {
    if (columnName === undefined) return undefined;
    return makeSourceColumnProperty(makeSourceColumn(columnName));
}

export function convertLegacySourceColumn(value: unknown | undefined): SourceColumn | undefined {
    if (typeof value === "string") {
        return makeSourceColumn(value);
    } else if (isSourceColumn(value)) {
        return value;
    }
    return undefined;
}

export function getSourceColumnProperty(propertyDesc: LegacyPropertyDescription | undefined): SourceColumn | undefined;
export function getSourceColumnProperty(
    propertyDesc: LegacyPropertyDescription | PropertyDescription | undefined
): SourceColumn | undefined;
export function getSourceColumnProperty(
    propertyDesc: LegacyPropertyDescription | PropertyDescription | undefined
): SourceColumn | undefined {
    if (propertyDesc === undefined) return undefined;
    if (typeof propertyDesc === "string") {
        return makeSourceColumn(propertyDesc);
    }
    if (!isPropertyDescription(propertyDesc)) return undefined;
    if (propertyDesc.kind !== PropertyKind.Column) return undefined;
    const { value } = propertyDesc;
    if (value === undefined) return undefined;

    return convertLegacySourceColumn(value);
}

export function getColumnProperty(propertyDesc: LegacyPropertyDescription | undefined): string | undefined;
export function getColumnProperty(
    propertyDesc: LegacyPropertyDescription | PropertyDescription | undefined
): string | undefined;
export function getColumnProperty(propertyDesc: LegacyPropertyDescription | undefined): string | undefined;
export function getColumnProperty(
    propertyDesc: LegacyPropertyDescription | PropertyDescription | undefined
): string | undefined;
export function getColumnProperty(
    propertyDesc: LegacyPropertyDescription | PropertyDescription | undefined
): string | undefined {
    const result = getSourceColumnProperty(propertyDesc);
    if (result === undefined) return undefined;
    return getDefaultContextColumnName(result);
}

export function makeTableProperty(tableName: TableName): PropertyDescription;
export function makeTableProperty(tableName: TableName | undefined): PropertyDescription | undefined;
export function makeTableProperty(tableName: TableName | undefined): PropertyDescription | undefined {
    if (tableName === undefined) return undefined;
    return { kind: PropertyKind.Table, value: tableName };
}

export function getTableProperty(propertyDesc: LegacyPropertyDescription | undefined): TableName | undefined {
    if (propertyDesc === undefined) return undefined;
    if (typeof propertyDesc === "string") return makeTableName(propertyDesc);
    const tableDesc = propertyDesc as PropertyDescription;
    const { kind, value } = tableDesc;
    if (kind !== PropertyKind.Table) return undefined;
    if (typeof value === "string") return makeTableName(value);
    return value as TableName;
}

export function makeStringProperty(value: string): PropertyDescription;
export function makeStringProperty(value: string | undefined): PropertyDescription | undefined;
export function makeStringProperty(value: string | undefined): PropertyDescription | undefined {
    if (value === undefined) return undefined;
    return { kind: PropertyKind.String, value };
}

export function getStringProperty<T extends string>(
    propertyDesc: LegacyPropertyDescription | undefined
): T | undefined {
    if (propertyDesc === undefined) return undefined;
    if (typeof propertyDesc === "string") return propertyDesc as T;
    if (!isPropertyDescription(propertyDesc)) return undefined;
    if (propertyDesc.kind !== PropertyKind.String) return undefined;
    if (propertyDesc.value === undefined) return undefined;
    return checkString(propertyDesc.value) as T;
}

export function makeNumberProperty(value: number): PropertyDescription {
    return { kind: PropertyKind.Number, value };
}

export function getNumberProperty(
    propertyDesc: PropertyDescription | LegacyPropertyDescription | undefined
): number | undefined {
    if (propertyDesc === undefined) return undefined;
    if (typeof propertyDesc === "number") return propertyDesc;
    if (!isPropertyDescription(propertyDesc)) return undefined;
    if (propertyDesc.kind !== PropertyKind.Number) return undefined;
    if (propertyDesc.value === undefined) return undefined;
    return checkNumber(propertyDesc.value);
}

export function makeSwitchProperty(value: boolean): PropertyDescription;
export function makeSwitchProperty(value: boolean | undefined): PropertyDescription | undefined;
export function makeSwitchProperty(value: boolean | undefined): PropertyDescription | undefined {
    if (value === undefined) return undefined;
    return { kind: PropertyKind.Switch, value };
}

export function getSwitchProperty(propertyDesc: LegacyPropertyDescription | undefined): boolean | undefined {
    if (propertyDesc === undefined) return undefined;
    if (typeof propertyDesc === "boolean") return propertyDesc;
    if (!isPropertyDescription(propertyDesc)) return undefined;
    if (propertyDesc.kind !== PropertyKind.Switch) return undefined;
    return checkBoolean(propertyDesc.value);
}

export function makeEnumProperty<P extends BasePrimitiveValue>(value: P): PropertyDescription;
export function makeEnumProperty<P extends BasePrimitiveValue>(value: P | undefined): PropertyDescription | undefined;
export function makeEnumProperty<P extends BasePrimitiveValue>(value: P | undefined): PropertyDescription | undefined {
    if (value === undefined) return undefined;
    return { kind: PropertyKind.Enum, value };
}

export function getEnumProperty<P extends BasePrimitiveValue>(
    propertyDesc: LegacyPropertyDescription | undefined
): P | undefined {
    if (propertyDesc === undefined) return undefined;
    if (isBasePrimitiveValue(propertyDesc)) {
        return propertyDesc as P;
    }
    if (!isPropertyDescription(propertyDesc)) return undefined;
    if (propertyDesc.kind !== PropertyKind.Enum) return undefined;
    if (!isBasePrimitiveValue(propertyDesc.value)) {
        return panic(`Enum value has the wrong type: ${typeof propertyDesc.value}`);
    }
    return propertyDesc.value as P;
}

export function getZapProperty(propertyDesc: PropertyDescription | undefined): string | undefined {
    if (propertyDesc === undefined) return undefined;
    if (propertyDesc.kind !== PropertyKind.Zap) return undefined;
    if (propertyDesc.value === undefined) return undefined;
    return checkString(propertyDesc.value) as string;
}

export function getWebhookProperty(propertyDesc: PropertyDescription | undefined): string | undefined {
    if (propertyDesc === undefined) return undefined;
    if (propertyDesc.kind !== PropertyKind.Webhook) return undefined;
    if (propertyDesc.value === undefined) return undefined;
    return checkString(propertyDesc.value) as string;
}

export function makeSpecialValueProperty(value: SpecialValueKind): PropertyDescription;
export function makeSpecialValueProperty(value: SpecialValueKind): PropertyDescription;
export function makeSpecialValueProperty(value: SpecialValueKind): PropertyDescription {
    return {
        kind: PropertyKind.SpecialValue,
        value,
    };
}

export function getSpecialValueProperty(
    propertyDesc: LegacyPropertyDescription | undefined
): SpecialValueKind | undefined {
    if (!isPropertyDescription(propertyDesc)) return undefined;
    if (propertyDesc?.kind !== PropertyKind.SpecialValue) return undefined;
    return checkString(propertyDesc.value) as SpecialValueKind;
}

export function makeScreenProperty(screenName: string): PropertyDescription {
    return { kind: PropertyKind.Screen, value: screenName };
}

export function getScreenProperty(propertyDesc: LegacyPropertyDescription | undefined): string | undefined {
    if (propertyDesc === undefined) return undefined;
    if (typeof propertyDesc === "string") return propertyDesc;
    if (!isPropertyDescription(propertyDesc)) return undefined;
    if (propertyDesc.kind !== PropertyKind.Screen) return undefined;
    if (propertyDesc.value === undefined) return undefined;
    return checkString(propertyDesc.value);
}

export function makeFilterProperty(value: ArrayFilter | undefined): PropertyDescription | undefined {
    if (value === undefined) return undefined;
    return { kind: PropertyKind.Filter, value };
}

export function getFilterProperty(propertyDesc: LegacyPropertyDescription): ArrayFilter;
export function getFilterProperty(propertyDesc: LegacyPropertyDescription | undefined): ArrayFilter | undefined;
export function getFilterProperty(propertyDesc: LegacyPropertyDescription | undefined): ArrayFilter | undefined {
    if (propertyDesc === undefined) return undefined;
    if (!isPropertyDescription(propertyDesc)) return propertyDesc as ArrayFilter;
    if (propertyDesc.kind !== PropertyKind.Filter) return undefined;
    return propertyDesc.value as ArrayFilter;
}

export function getPaymentMethodProperty(propertyDesc: PropertyDescription | undefined): PaymentMethod | undefined {
    if (propertyDesc === undefined) return undefined;
    if (propertyDesc.kind !== PropertyKind.PaymentMethod) return undefined;
    if (propertyDesc.value === undefined) return undefined;
    if (!isPaymentMethod(propertyDesc.value)) return undefined;
    return propertyDesc.value;
}

export function makeArrayProperty<T>(items: readonly T[]): PropertyDescription {
    return { kind: PropertyKind.Array, value: items };
}

export function getArrayProperty<T>(propertyDesc: PropertyDescription | undefined): readonly T[] | undefined {
    if (propertyDesc === undefined) return undefined;
    if (propertyDesc.kind !== PropertyKind.Array) return undefined;
    if (propertyDesc.value === undefined) return undefined;
    return checkArray(propertyDesc.value);
}

export function makeIconProperty(iconName: Icon): PropertyDescription {
    return { kind: PropertyKind.Icon, value: iconName };
}

export function getIconProperty(propertyDesc: PropertyDescription | undefined): Icon | undefined {
    if (propertyDesc?.kind !== PropertyKind.Icon) return undefined;
    return checkString(propertyDesc.value);
}

export function makeEmojiProperty(emoji: string): PropertyDescription {
    return { kind: PropertyKind.Emoji, value: emoji };
}

export function getEmojiProperty(propertyDesc: PropertyDescription | undefined): string | undefined {
    if (propertyDesc?.kind !== PropertyKind.Emoji) return undefined;
    return checkString(propertyDesc.value);
}

export function makeActionProperty(action: ActionDescription): PropertyDescription {
    return { kind: PropertyKind.Action, value: action };
}

export function getActionProperty(propertyDesc: LegacyPropertyDescription | undefined): ActionDescription | undefined {
    if (isArray(propertyDesc)) {
        return propertyDesc[0] as ActionDescription | undefined;
    }
    if (!isPropertyDescription(propertyDesc)) return undefined;
    if (propertyDesc?.kind !== PropertyKind.Action) return undefined;
    if (isArray(propertyDesc.value)) {
        return propertyDesc.value[0] as ActionDescription | undefined;
    }
    return propertyDesc.value as ActionDescription;
}

export function makeCompoundActionProperty(actionID: string): PropertyDescription {
    return { kind: PropertyKind.CompoundAction, value: actionID };
}

export function getCompoundActionProperty(propertyDesc: PropertyDescription | undefined): string | undefined {
    if (propertyDesc?.kind !== PropertyKind.CompoundAction) return undefined;
    return checkString(propertyDesc.value);
}
